<template>
    <div class="table-responsive">
        <loader v-if="isLoading" />
        <b-table-simple v-else borderless hover class="list">
            <colgroup>
                <col v-if="invoiceActivities.length > 0" style="width: 2%;" />
                <col style="width: 5%;" />
                <col style="width: 40%;" />
                <col style="width: 25%;" />
                <col style="width: 20%;" />
                <col style="width: 2%;" />
            </colgroup>
            <b-thead>
                <b-tr>
                    <b-th v-if="invoiceActivities.length > 0"></b-th>
                    <b-th>Type:</b-th>
                    <b-th>Description:</b-th>
                    <b-th>Due:</b-th>
                    <b-th>Repeat:</b-th>
                    <b-th></b-th>
                </b-tr>
            </b-thead>
            <b-tbody v-if="invoiceActivities.length == 0">
                <b-tr>
                    <td colspan="5">
                        <i>No records available</i>
                    </td>
                </b-tr>
            </b-tbody>
            <draggable
                v-else
                v-model="invoiceActivities"
                tag="tbody"
                ghost-class="sort-row-ghost"
                drag-class="sort-row-drag"
                :animation="0"
                handle=".handle"
                @start="isRowDragging = true"
                @end="isRowDragging = false"
                @sort="activitiesSorted"
            >
                <tr v-for="activity in invoiceActivities" :key="activity.id">
                    <td class="handle">
                        <span
                            class="cus-black-white-swatch cursor-move disable-touch-select"
                        ></span>
                    </td>
                    <td>
                        <span>{{
                            TEMPLATES.ACTIVITY_TYPE_STR[activity.activity_type]
                        }}</span>
                    </td>
                    <td>
                        <span
                            v-if="
                                activity.activity_type ==
                                TEMPLATES.ACTIVITY_TYPE.MEDICAL
                            "
                        >
                            <router-link
                                v-if="activity.item"
                                :to="{
                                    name: 'item',
                                    params: {
                                        id: activity.item.encoded_id
                                    }
                                }"
                                target="_blank"
                                >{{ activity.item.name }}</router-link
                            >
                        </span>
                        <span
                            v-else-if="
                                activity.activity_type ==
                                TEMPLATES.ACTIVITY_TYPE.OTHER
                            "
                        >
                            {{
                                activity.activity
                                    ? labelOptions[
                                          Number(activity.activity.label_id)
                                      ]
                                    : ""
                            }}
                        </span>
                        <span v-else>
                            {{
                                prepareVitalsNaming(activity.master_vital)
                            }}</span
                        >
                    </td>
                    <td>{{ prepareDueRepeat(activity.activity, "due") }}</td>
                    <td>{{ prepareDueRepeat(activity.activity, "repeat") }}</td>
                    <td class="text-right small text-nowrap">
                        <span
                            class="cursor-pointer"
                            @click="editActivity(activity)"
                        >
                            Edit
                        </span>
                        <span>
                            |
                        </span>
                        <span
                            @click="deleteActivity(activity.id)"
                            class="cursor-pointer"
                        >
                            Delete
                        </span>
                    </td>
                </tr>
            </draggable>
        </b-table-simple>
        <modal-delete ref="modalDelete" @reload="getActivities" />
    </div>
</template>

<script>
/**
 * @name TemplateInvoiceActivityList
 * @description List of template Invoice Activity
 * @see apps/application/views/staff/business/template/sub/navbar.php
 * @see apps/assets/js/module/business/element.js
 * @see apps/application/views/staff/business/template/sub/activity/create_update.php
 */
import TEMPLATES from "@/constants/templates";
import TEMPLATE_SERVICES from "@/services/templates";
import { map, mapValues, keyBy } from "lodash";
import draggable from "vuedraggable";

export default {
    mixins: [],
    components: {
        draggable,
        Loader: () => import("@/components/elements/loader-list"),
        ModalDelete: () =>
            import(
                "@/components/setting-template/bundle-invoice/modal/delete-activity"
            )
    },
    props: {
        invoice: {
            type: Object,
            required: false
        }
    },
    computed: {
        templateId() {
            return this.$store.state.settingsTemplate.templateId;
        },
        templateType() {
            return this.$route.params.type;
        },
        labelOptions() {
            return mapValues(
                keyBy(
                    this.$store.state.reference["treatment-task-label"],
                    "value"
                ),
                "text"
            );
        }
    },
    watch: {},
    data() {
        return {
            TEMPLATES,
            isLoading: false,
            showPagination: false,
            invoiceActivities: [],
            isRowDragging: false,
            otherActivityLabel: null
        };
    },
    mounted() {
        this.getActivities();
    },
    destroyed() {},
    methods: {
        async getActivities() {
            try {
                this.isLoading = true;

                const res = await TEMPLATE_SERVICES.getActivities(
                    this.templateId
                );

                if (res.message?.code == 200) {
                    this.invoiceActivities = res.resources;
                } else {
                    throw new Error("Error: Unable to fetch activities");
                }
            } catch (error) {
                this._message(error.message, "danger");
            } finally {
                this.$emit("reload-tab-count");
                this.isLoading = false;
            }
        },
        async activitiesSorted() {
            const params = {
                id: this.templateId,
                data: this.invoiceActivities.map(invAct => invAct.id)
            };
            await TEMPLATE_SERVICES.updateInvoiceTemplateActivitiesOrder(params)
                .then(res => {
                    if (res.message?.code != 200) {
                        throw "Error: Cannot reorder invoice bundle activities";
                    }
                })
                .catch(error => {
                    this._message(error, "danger");
                });
        },
        editActivity(activity) {
            this.$emit("update-activity", activity);
        },
        deleteActivity(id) {
            this.$refs.modalDelete.show(id);
        },
        prepareDueRepeat(activity, type) {
            if (type == "due") {
                if (activity.due.type === 2) {
                    return "At " + activity.due.specific_time;
                } else {
                    return (
                        activity.due.after_start_time +
                        " minute(s) after start of treatment"
                    );
                }
            }

            if (type == "repeat") {
                if (typeof activity.repeat !== "undefined") {
                    return (
                        "Every " +
                        activity.repeat.specific_interval +
                        " minutes(s) " +
                        activity.repeat.occurence +
                        " times"
                    );
                } else {
                    return "No repeat";
                }
            }
        },
        prepareVitalsNaming(vitals) {
            return map(vitals, "name").join(", ");
        }
    }
};
</script>
